import { ApiHttpError } from '@/shared/api/client';
import { z } from 'zod';
import type { RestartUploadResponse, UploadTask } from '../types/uploads';

const resumableNotExpiredSchema = z
  .object({
    error: z.literal('resumable_not_expired'),
    offset: z.string().regex(/^\d+$/),
  })
  .strict();

/** A 404 may mean missing/hidden submission; only the backend's 410 proves expiry. */
export function hasUploadHttpStatus(error: unknown, status: number): boolean {
  const seen = new Set<unknown>();
  let current = error;
  while (current && typeof current === 'object' && !seen.has(current)) {
    seen.add(current);
    if (current instanceof ApiHttpError && current.status === status) return true;
    current = (current as { cause?: unknown }).cause;
  }
  return false;
}

/** The backend's 409 offset is authoritative evidence that the old URI is still live. */
export function resumableNotExpiredOffset(error: unknown, fileSize: number): number | undefined {
  if (!(error instanceof ApiHttpError) || error.status !== 409) return undefined;
  const parsed = resumableNotExpiredSchema.safeParse(error.payload);
  if (!parsed.success) return undefined;
  const offset = Number(parsed.data.offset);
  return Number.isSafeInteger(offset) && offset >= 0 && offset <= fileSize ? offset : undefined;
}

/** Reject a response for another file/project before adopting its session as the upload writer. */
export function validateRestartResponse(
  item: UploadTask,
  contentBlake3: string,
  previousSessionId: string,
  replacementCandidateId: string,
  result: RestartUploadResponse,
): void {
  const previous = result.previousSession;
  const session = result.session;
  const mediaType = item.mediaType ?? 'video';
  if (
    previous.sessionId !== previousSessionId ||
    previous.contentBlake3 !== contentBlake3 ||
    previous.projectId !== item.target.projectId ||
    previous.declaredBytes !== item.totalBytes ||
    previous.mediaType !== mediaType ||
    previous.originalFilename !== item.displayName ||
    session.contentBlake3 !== contentBlake3 ||
    session.projectId !== item.target.projectId ||
    session.declaredBytes !== item.totalBytes ||
    session.mediaType !== mediaType ||
    session.originalFilename !== item.displayName
  ) {
    throw new Error('restart_response_identity_mismatch');
  }

  if (result.action === 'completed') {
    if (session.sessionId !== previousSessionId || session.status !== 'uploaded') {
      throw new Error('restart_response_completion_mismatch');
    }
    return;
  }

  if (
    previous.status !== 'abandoned' ||
    previous.replacedBySessionId !== session.sessionId ||
    session.sessionId === previousSessionId ||
    session.status === 'abandoned' ||
    session.status === 'failed' ||
    (result.action === 'restarted' && session.sessionId !== replacementCandidateId)
  ) {
    throw new Error('restart_response_replacement_mismatch');
  }
}
